import { Base } from '@studiometa/js-toolkit';
import type { BaseProps } from '@studiometa/js-toolkit';
import { domScheduler, memo, nextTick } from '@studiometa/js-toolkit/utils';
import ScrollAnimation from './ScrollAnimation/ScrollAnimation.js';

export interface SplitLinesProps extends BaseProps {
  $el: HTMLElement;
  $refs: {
    paragraph: HTMLElement;
    template: HTMLTemplateElement;
  };
}

/**
 * Composant SplitLines.
 * Divise un paragraphe en lignes et applique un template HTML à chaque ligne.
 */
export default class SplitLines extends Base<SplitLinesProps> {
  static config = {
    name: 'SplitLines',
    refs: ['paragraph', 'template'],
    components: {
      ScrollAnimation,
    },
  };

  lastWindowWidth: number = 0;

  /**
   * Contenu textuel original du paragraphe.
   */
  textContent: string = '';

  /**
   * Dernière largeur mesurée du paragraphe.
   */
  lastWidth: number = 0;

  /**
   * Mémorise le calcul des lignes pour éviter de recalculer si rien n'a changé.
   * @param {...unknown[]} args - Arguments [texte, largeur]
   * @returns {string[]} Liste des lignes de texte
   */
  getLines = memo((...args: unknown[]) => {
    const [text, width] = args as [string, number];

    // Créer un clone pour analyser le texte
    const clone = this.$refs.paragraph.cloneNode(true) as HTMLElement;
    clone.style.cssText = 'position: absolute; visibility: hidden; pointer-events: none;';
    clone.textContent = text as string;
    document.body.append(clone);
    clone.style.width = `${width}px`;

    // Obtenir les positions de chaque caractère
    const positions: Array<{ char: string; top: number; left: number }> = [];
    const range = document.createRange();

    // Préparation du contenu texte
    if (clone.childNodes.length === 0 || clone.childNodes[0].nodeType !== Node.TEXT_NODE) {
      clone.innerHTML = '';
      clone.append(document.createTextNode(text));
    }

    const textNode = clone.firstChild as ChildNode;

    // Mesurer chaque caractère plus efficacement par batch de 10 caractères
    text.split('').forEach((char, i) => {
      range.setStart(textNode, i);
      range.setEnd(textNode, i + 1);
      const rect = range.getBoundingClientRect();
      positions.push({ char, top: rect.top, left: rect.left });
    });

    // Supprimer le clone
    clone.remove();

    // Trouver les lignes en groupant par position top
    const lines: string[] = [];
    let currentLine: Array<{ char: string; top: number; left: number }> = [];
    let currentTop = positions[0]?.top;

    positions.forEach((pos) => {
      // Tolérance de 2px pour les différences de rendu
      if (Math.abs(pos.top - currentTop) > 2) {
        if (currentLine.length > 0) {
          lines.push(currentLine.map((p) => p.char).join(''));
        }
        currentLine = [pos];
        currentTop = pos.top;
      } else {
        currentLine.push(pos);
      }
    });

    // Ajouter la dernière ligne
    if (currentLine.length > 0) {
      lines.push(currentLine.map((p) => p.char).join(''));
    }

    return lines.filter((line) => line.trim().length > 0);
  });

  /**
   * Initialise le composant après son montage dans le DOM.
   */
  async mounted() {
    this.lastWindowWidth = window.innerWidth;

    const paragraph = this.$refs.paragraph;
    const template = this.$refs.template;
    this.textContent = paragraph?.textContent || '';

    if (!paragraph || !template || !this.textContent.length) {
      this.$destroy();
      return;
    }

    await nextTick();
    this.init();
  }

  /**
   * Réinitialise le composant lorsque la taille de l'écran change.
   */
  async resized() {
    if (window.innerWidth === this.lastWindowWidth) {
      return;
    }

    this.lastWindowWidth = window.innerWidth;
    this.init();
  }

  /**
   * Nettoie le composant lors de sa destruction.
   */
  destroyed() {
    this.reset();
  }

  /**
   * Réinitialise le contenu du paragraphe à son état original.
   */
  reset() {
    this.$refs.paragraph.textContent = this.textContent;
  }

  /**
   * Initialise le composant en divisant le texte en lignes et en appliquant le template.
   */
  init() {
    domScheduler.read(() => {
      const { width } = this.$refs.paragraph.getBoundingClientRect();
      this.lastWidth = width;

      // Utiliser la fonction mémorisée pour obtenir les lignes
      const lines = this.getLines(this.textContent, width);

      domScheduler.write(() => {
        // Mise à jour du contenu avec le template
        const templateContent = this.$refs.template.innerHTML;
        this.$refs.paragraph.innerHTML = lines
          .map((line) => templateContent.replace('%s', line))
          .join('');

        // Mettre à jour le composant après le rendu
        nextTick().then(() => {
          this.$update();
        });
      });
    });
  }
}
